import {
  AfterViewInit,
  Component,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { BlogService, Blog } from '../services/blog.service';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UtilsService } from '@geekeno/ngx-website/services';
import { BlogListComponent } from '../blog-list/blog-list.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-blog-list-page',
  imports: [RouterModule, BlogListComponent],
  templateUrl: './blog-list-page.component.html',
  styleUrl: './blog-list-page.component.scss',
})
export class BlogListPageComponent implements AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private blogService = inject(BlogService);
  private utils = inject(UtilsService);

  // Convert route data to signal
  private routeData = toSignal(this.route.data, {
    initialValue: {} as {
      initialBlogs: { results: Blog[]; next: string | null };
    },
  });

  // Reactive state with signals
  blogs = signal<Blog[]>([]);
  loading = signal(false);
  error = signal('');
  currentPage = signal(0);
  pageSize = signal(4);
  allLoaded = signal(false);

  private intersectionObserver!: IntersectionObserver;

  constructor() {
    // Initialize from resolver data
    effect(() => {
      const data = this.routeData().initialBlogs;
      if (data && this.blogs().length === 0) {
        this.blogs.set(data.results);
        this.currentPage.set(1);
        if (!data.next) {
          this.allLoaded.set(true);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.utils.isPlatformBrowser) {
      this.pageSize.set(window.outerWidth > 2500 ? 6 : 4);
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.loading() && !this.allLoaded()) {
          this.currentPage.update((page) => page + 1);
          this.fetchBlogs();
        }
      });
      const footer = document.querySelector('.footer-wrapper')!;
      this.intersectionObserver.observe(footer);
    }
  }

  fetchBlogs() {
    if (this.allLoaded()) return;

    this.loading.set(true);
    this.blogService.getBlogs(this.currentPage(), this.pageSize()).subscribe({
      next: (data) => {
        this.blogs.update((current) => [...current, ...data.results]);
        this.loading.set(false);
        if (!data.next) {
          this.allLoaded.set(true);
          this.disconnectObserver();
        }
      },
      error: (err) => {
        this.error.set('Failed to load blogs');
        this.loading.set(false);
        console.error(err);
      },
    });
  }

  disconnectObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
  }
}
